import { useRef, useState } from "react";
import { toPng, toBlob } from "html-to-image";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "");

export function ProtectionCard({ photoCount, lockedAt }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const render = () => toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });

  const download = async () => {
    setBusy(true);
    try {
      const url = await render();
      const a = document.createElement("a");
      a.href = url; a.download = "tenantseal-protected.png"; a.click();
    } catch (e) { console.error(e); toast.error("Couldn't generate the image."); }
    finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const blob = await toBlob(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const file = new File([blob], "tenantseal-protected.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Documented & protected",
          text: "This home is documented & protected with TenantSeal.",
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "tenantseal-protected.png"; a.click();
        URL.revokeObjectURL(url);
        toast.success("Image downloaded — share it anywhere.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") { console.error(e); toast.error("Sharing isn't available — try Download."); }
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6" data-testid="protection-card-section">
      <h2 className="font-heading text-xl font-semibold text-navy">Share your protection</h2>
      <p className="mt-1 text-sm text-[#64748B]">A shareable badge for group chats and socials. No address or photos — just proof you're covered.</p>

      {/* The card that gets exported */}
      <div className="mt-4 flex justify-center">
        <div
          ref={cardRef}
          data-testid="protection-card"
          className="relative w-[340px] h-[340px] rounded-3xl overflow-hidden"
          style={{ background: "#0B1F3A" }}
        >
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 20%, rgba(245,158,11,0.18), transparent 55%)" }} />
          <div className="absolute -right-10 -bottom-10 opacity-10">
            <ShieldCheck size={220} color="#F59E0B" strokeWidth={1.2} />
          </div>
          <div className="relative h-full flex flex-col items-center justify-center px-8 text-center">
            <span className="grid place-items-center h-16 w-16 rounded-2xl" style={{ background: "rgba(245,158,11,0.15)" }}>
              <ShieldCheck size={38} color="#F59E0B" />
            </span>
            <p className="mt-5 font-heading text-2xl font-bold leading-tight" style={{ color: "#FFFFFF" }}>
              This home is documented &amp; protected
            </p>
            <p className="mt-3 text-sm" style={{ color: "#CBD5E1" }}>
              {photoCount} photo{photoCount === 1 ? "" : "s"} · locked {fmt(lockedAt)}
            </p>
            <div className="mt-5 h-px w-24" style={{ background: "rgba(245,158,11,0.5)" }} />
            <p className="mt-4 text-xs font-semibold tracking-wide" style={{ color: "#F59E0B" }}>tenantseal.com</p>
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-center gap-3">
        <Button data-testid="protection-share-btn" onClick={share} disabled={busy} className="rounded-xl bg-brand hover:bg-brand-hover text-white">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Share2 size={16} className="mr-2" />} Share
        </Button>
        <Button data-testid="protection-download-btn" onClick={download} disabled={busy} variant="outline" className="rounded-xl">
          <Download size={16} className="mr-2" /> Download
        </Button>
      </div>
    </div>
  );
}
